import { getWebUrl } from "@areahub/config";
import { Link, ProPlanModal } from "@areahub/ui";
import { Button, ButtonProps } from "@chakra-ui/react";
import { ButtonProp } from "lib/gcms";
import { GcmsButtonFragment } from "lib/gcms/fragment-typings";
import { RefObject, useState } from "react";

export type MapButtonProps = ButtonProps & {
  /**
   * Determines if the button acts as a navigation link.
   */
  navLink?: Boolean;
  /**
   * Specifies the navigation type if `navLink` is `true`.
   */
  navLinkType?: "desktop" | "mobile";
  /**
   * Ref used for scrolling behavior.
   */
  scrollRef?: RefObject<null>;
};

const PopupButton: React.FC<{
  data: Partial<GcmsButtonFragment>;
  props: MapButtonProps;
}> = ({ data, props }) => {
  const [isOpen, setOpen] = useState(false);
  const onClose = () => {
    setOpen(false);
  };
  return (
    <>
      <Button key={data.id} {...props} onClick={() => setOpen(true)}>
        {data.label}
      </Button>
      <ProPlanModal {...{ isOpen, onClose }} />
    </>
  );
};

/**
 * A dynamic component that renders different button components based on the provided `buttonAction` value from the `data` prop.
 *
 * @returns The selected button component depending on the `buttonAction`, or a standard <Button> component if there is no match found.
 */
export function mapButton(
  /**
   * the data associated with the button component which include `label`, `buttonAction`, `theme`, `variant`
   */
  data: ButtonProp,
  { navLink, navLinkType, scrollRef, ...props }: MapButtonProps,
) {
  const buttonProps: MapButtonProps = {
    variant: data?.variant || "solid",
    colorScheme: data?.theme || "blue",
    _focus: {
      outline: "none",
    },
    _focusVisible: {
      outline: "none !important",
    },
    borderRadius: "50px",
    ...props,
  };

  if (buttonProps.variant === "link" && navLinkType) {
    if (buttonProps.colorScheme !== "black_alpha") {
      buttonProps["color"] = `${buttonProps.colorScheme}.500`;
      buttonProps["_hover"] = {
        color: `${buttonProps.colorScheme}.700`,
      };
    }
    delete buttonProps?.colorScheme;
  }

  switch (data.buttonAction) {
    case "SCROLL":
      const scrollToRef = scrollRef
        ? () => {
            window.scrollTo({
              // The nav bar on mobile has height 56, so subtract that here
              //@ts-ignore
              top: scrollRef.current.offsetTop,
              behavior: "smooth",
            });
          }
        : undefined;
      return (
        <Button key={data.id} {...buttonProps} onClick={scrollToRef}>
          {data.label}
        </Button>
      );
    case "OPEN_MODAL":
      return <PopupButton key={data.id} {...{ data }} props={buttonProps} />;
    case "OPEN_SEARCH":
      return (
        <Link
          href={getWebUrl() + "/individuals/pricing"}
          key={data.id}
          _hover={{ textDecoration: "none" }}
        >
          <Button textDecoration="none" {...buttonProps}>
            {data.label}
          </Button>
        </Link>
      );
    case "CLOSE_MODAL":
      return <PopupButton key={data.id} {...{ data }} props={buttonProps} />;
    case "EXTERNAL_LINK":
    case "PAGE_LINK":
    case "LINK":
      const isExternal = data.buttonAction === "EXTERNAL_LINK" || undefined;
      return (
        <Link
          href={data.href || "/individuals"}
          key={data.id}
          isExternal={isExternal}
          _hover={{ textDecoration: "none" }}
        >
          <Button
            {...buttonProps}
            textDecoration="none"
            fontSize={{
              base: "14px",
              md: "16px",
              lg: "17px",
              xl: "18px",
            }}
            py={{
              base: "6",
              lg: "8",
            }}
            px={"5"}
          >
            {data.label}
          </Button>
        </Link>
      );
    default:
      return <Button {...buttonProps}>{data.label}</Button>;
  }
}
